import { useEffect, useState } from 'react'

import { DevToolsModal } from '@/features/dev-tools/components/dev-tools-modal'
import { FeatureFlagsTool } from '@/features/dev-tools/components/feature-flags-tool'
import { DEV_TOOLS, type DevToolId } from '@/features/dev-tools/domain/dev-tools'
import { MatrixTool } from '@/features/dev-tools/containers/matrix-tool'

// Global dev-tools entry point, mounted once in App. Cmd/Ctrl+D toggles the
// modal from anywhere (preventDefault keeps the browser's bookmark dialog out
// of the way); while open, each tool's own shortcut (see DEV_TOOLS) opens its
// panel. Closing always returns to the tool list.
export function DevTools() {
  const [open, setOpen] = useState(false)
  const [activeTool, setActiveTool] = useState<DevToolId | null>(null)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd') {
        event.preventDefault()
        setOpen(previous => !previous)
        setActiveTool(null)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      // Never hijack keystrokes typed into an editable element — e.g. Shift+F
      // must still produce a capital F in the feature-flags search input.
      const target = event.target
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return
      }
      const tool = DEV_TOOLS.find(candidate => candidate.matchesShortcut(event))
      if (tool) {
        event.preventDefault()
        setActiveTool(tool.id)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open])

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen) setActiveTool(null)
  }

  function handleClose() {
    setOpen(false)
    setActiveTool(null)
  }

  const panel =
    activeTool === 'feature-flags' ? (
      <FeatureFlagsTool onBack={() => setActiveTool(null)} onClose={handleClose} />
    ) : activeTool === 'matrix' ? (
      <MatrixTool onBack={() => setActiveTool(null)} />
    ) : null

  return (
    <DevToolsModal
      open={open}
      activeTool={activeTool}
      panel={panel}
      onOpenChange={handleOpenChange}
      onSelectTool={setActiveTool}
    />
  )
}
